<template>
	<defs>
		<!-- Light mode gradient -->
		<linearGradient
			:id="`buttonGradient-${uid}`"
			gradientUnits="objectBoundingBox"
			gradientTransform="rotate(145)"
		>
			<stop
				offset="0%"
				stop-color="#ffffff"
			/>
			<stop
				offset="100%"
				stop-color="#F0F0F0"
			/>
		</linearGradient>

		<!-- Spinning rim glow gradient for loading state -->
		<linearGradient
			:id="`rimGlowLoading-${uid}`"
			x1="0%"
			y1="0%"
			x2="100%"
			y2="0%"
		>
			<stop offset="0%" stop-color="rgba(200, 200, 200, 0.3)" />
			<stop offset="30%" stop-color="rgba(200, 200, 200, 0.3)" />
			<stop offset="50%" stop-color="rgba(59, 130, 246, 0.8)" />
			<stop offset="70%" stop-color="rgba(200, 200, 200, 0.3)" />
			<stop offset="100%" stop-color="rgba(200, 200, 200, 0.3)" />
			<animateTransform
				attributeName="gradientTransform"
				type="rotate"
				from="0 0.5 0.5"
				to="360 0.5 0.5"
				dur="3s"
				repeatCount="indefinite"
			/>
		</linearGradient>

		<!-- Static rim glow - result color (solid 360° color, no animation) -->
		<linearGradient
			v-if="resultColor"
			:id="`rimGlowResult-${uid}`"
			x1="0%"
			y1="0%"
			x2="100%"
			y2="0%"
		>
			<stop offset="0%" :stop-color="resultColor" />
			<stop offset="100%" :stop-color="resultColor" />
		</linearGradient>

		<!-- Dark mode gradient -->
		<linearGradient
			:id="`buttonGradientDark-${uid}`"
			gradientUnits="objectBoundingBox"
			gradientTransform="rotate(145)"
		>
			<stop
				offset="0%"
				stop-color="#18272B"
			/>
			<stop
				offset="100%"
				stop-color="#293d43"
			/>
		</linearGradient>

		<!-- Light mode border filter: intense shadow and highlight slopes -->
		<filter :id="`borderEffectsLight-${uid}`" x="-200%" y="-200%" width="500%" height="500%">
			<!-- Shadow extending down and right - more intense and further -->
			<feGaussianBlur in="SourceAlpha" stdDeviation="14" result="blur1" />
			<feOffset in="blur1" dx="8" dy="8" result="offsetBlur1" />
			<feComponentTransfer in="offsetBlur1" result="shadow">
				<feFuncA type="linear" slope="0.28" />
			</feComponentTransfer>
			<!-- Highlight extending up and left - more intense and further -->
			<feGaussianBlur in="SourceAlpha" stdDeviation="12" result="blur2" />
			<feOffset in="blur2" dx="-7" dy="-7" result="offsetBlur2" />
			<feFlood flood-color="#ffffff" result="whiteFlood" />
			<feComposite in="whiteFlood" in2="offsetBlur2" operator="in" result="highlight" />
			<feComponentTransfer in="highlight" result="highlight">
				<feFuncA type="linear" slope="0.45" />
			</feComponentTransfer>
			<!-- Combine all effects -->
			<feMerge>
				<feMergeNode in="shadow" />
				<feMergeNode in="highlight" />
				<feMergeNode in="SourceGraphic" />
			</feMerge>
		</filter>

		<!-- Dark mode border filter: very intense dark shadow slope -->
		<filter :id="`borderEffectsDark-${uid}`" x="-200%" y="-200%" width="500%" height="500%">
			<!-- Shadow extending down and right - very intense and extended -->
			<feGaussianBlur in="SourceAlpha" stdDeviation="16" result="blur1" />
			<feOffset in="blur1" dx="10" dy="10" result="offsetBlur1" />
			<feComponentTransfer in="offsetBlur1" result="shadow">
				<feFuncA type="linear" slope="0.5" />
			</feComponentTransfer>
			<!-- Subtle highlight extending up and left -->
			<feGaussianBlur in="SourceAlpha" stdDeviation="8" result="blur2" />
			<feOffset in="blur2" dx="-5" dy="-5" result="offsetBlur2" />
			<feFlood flood-color="#ffffff" result="whiteFlood" />
			<feComposite in="whiteFlood" in2="offsetBlur2" operator="in" result="highlight" />
			<feComponentTransfer in="highlight" result="highlight">
				<feFuncA type="linear" slope="0.15" />
			</feComponentTransfer>
			<!-- Combine all effects -->
			<feMerge>
				<feMergeNode in="shadow" />
				<feMergeNode in="highlight" />
				<feMergeNode in="SourceGraphic" />
			</feMerge>
		</filter>
	</defs>
</template>

<script setup lang="ts">
defineProps<{
	/** Unique identifier for filter IDs */
	uid: string
	/** Optional result color for static rim glow */
	resultColor?: string | null
}>()
</script>
